import React, { useCallback, useRef, useState } from 'react';
import { KatAlert, KatButton, KatModal } from '@amzn/katal-react';
import { useQueryClient } from 'react-query';
import { GSLAToPublish } from 'src/features/types/gslaApi';
import { publishGSLAValues } from 'src/utils/Queries';
import { useUser } from 'src/utils/UserContext';
import { ConfirmationModal } from './ConfirmationModal';
import { GSLAUploadRow } from '../../types/bulkGslaTypes';
import { parseCSV, validateData } from '../../utils/csvHelpers';
import { validateForDefaultPCSafety, logDefaultPCSafetyViolation } from '../../utils/defaultPCSafety';
export const UploadButton: React.FC = () => {
    const [isUploading, setIsUploading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<GSLAToPublish[]>([]);
    const [uploadedData, setUploadedData] = useState<GSLAUploadRow[]>([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { user } = useUser();
    const queryClient = useQueryClient();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;


        setIsUploading(true);
        try {
            const text = await file.text();
            const parsedData = parseCSV(text);

            if (!validateData(parsedData)) {
                throw new Error('Invalid data format in CSV');
            }

            setUploadedData(parsedData);

            const changes: GSLAToPublish[] = parsedData
                .filter(row => row.overrideValue && !isNaN(Number(row.overrideValue)) && Number(row.overrideValue) > 0)
                .map(row => ({
                    warehouseId: row.warehouseId,
                    userAlias: user?.login ?? 'unknown', // This should be set to the current user's alias
                    publishDateEpochMillis: Date.now(),
                    actionSource: 'BULK_UPLOAD',
                    reason: 'Bulk CSV Upload',
                    productionCapability: row.productionCapability,
                    gslaInSeconds: Number(row.overrideValue),
                    warehouseHandlingTier: row.warehouseHandlingTier
                }));

            // CRITICAL SAFETY CHECK: Validate for Default_PC entries
            const safetyCheck = validateForDefaultPCSafety(changes, 'GSLA', 'CSV Upload');
            
            if (safetyCheck.shouldBlock) {
                // Log the safety violation for audit purposes
                logDefaultPCSafetyViolation(
                    'GSLA',
                    user?.login || 'unknown',
                    safetyCheck.defaultPCCount,
                    'CSV Upload'
                );
                
                throw new Error(`CRITICAL SAFETY BLOCK: Your CSV contains ${safetyCheck.defaultPCCount} "Default_PC" entries. "Default_PC" cannot be published as it would cause serious system issues. Please remove all "Default_PC" entries from your CSV file and try again.`);
            }

            if (changes.length > 0) {
                setPendingChanges(changes);
                setIsModalOpen(true);
            } else {
                throw new Error('No valid override values found in CSV');
            }

        } catch (error) {
            console.error('Error processing CSV file:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to process CSV file');
            setShowErrorModal(true);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, []);



    const handleConfirmChanges = async () => {
        try {
            // ADDITIONAL SAFETY CHECK: Double-validate before final publish
            const finalSafetyCheck = validateForDefaultPCSafety(pendingChanges, 'GSLA', 'Final Validation');
            
            if (finalSafetyCheck.shouldBlock) {
                // This should not happen if the earlier validation worked, but it's a safety net
                throw new Error(`CRITICAL SAFETY ERROR: Default_PC detected in final publish validation. Operation aborted.`);
            }
            
            console.log('Publishing GSLA values with pendingChanges:', pendingChanges);
            console.log('Request body:', { gslaToPublishList: pendingChanges });
            
            const response = await publishGSLAValues({
                gslaToPublishList: pendingChanges,
                preserveDefaultTypes: true
            });
            console.log('Publish response:', response);
            
            // Extract unique warehouse IDs from the pending changes
            const warehouseIds = Array.from(new Set(pendingChanges.map(change => change.warehouseId)));
            
            // Invalidate the relevant queries to refresh the main table
            await queryClient.invalidateQueries(['gsla-multiple-warehouses']);
            await queryClient.invalidateQueries(['gsla-recommendations-multiple']);
            
            // Also invalidate specific warehouse queries
            warehouseIds.forEach(warehouseId => {
                queryClient.invalidateQueries(['gsla-single-warehouse', warehouseId]);
                queryClient.invalidateQueries(['gsla-recommendations-single', warehouseId]);
            });
            
            setIsModalOpen(false);
            setPendingChanges([]);
            setUploadedData([]);
            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error publishing values:', error);
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save changes. Please try again.');
            setIsModalOpen(false);
            setShowErrorModal(true);
        }
    };

    const handleButtonClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv"
                style={{ display: 'none' }}
            />
            <KatButton
                variant="primary"
                onClick={handleButtonClick}
                disabled={isUploading}
            >
                {isUploading ? 'Uploading...' : 'Upload CSV'}
            </KatButton>

            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                pendingChanges={pendingChanges}
                uploadedData={uploadedData}
                onConfirm={handleConfirmChanges}
                isUploading={isUploading}
            />
            {/* Success Modal */}
            <KatModal
                visible={showSuccessModal}
                title="Success"
            >
                <div>
                    <p>Changes have been saved successfully</p>
                    <div>
                        <KatButton
                            onClick={() => setShowSuccessModal(false)}
                            variant="primary"
                        >
                            OK
                        </KatButton>
                    </div>
                </div>
            </KatModal>
            {/* Error Modal */}
            <KatModal
                visible={showErrorModal}
                title="Error"
            >
                <div>
                    <p> An error occurred while saving changes. Please try again.</p>
                    <p style={{ color: '#D13212' }}>{errorMessage}</p>
                    <div>
                        <KatButton
                            onClick={() => setShowErrorModal(false)}
                            variant="primary"
                        >
                            OK
                        </KatButton>
                    </div>
                </div>
            </KatModal>
        </>
    );
};
